import React, { useState } from 'react';
import { useGetSurveyStonesQuery, downloadExport } from 'state';
import { getAccountToday } from 'utils';
import { HighRiskTerm } from '../HighRiskTerm';
import {
  STONE_CLASSES,
  MUST_PICK_COLOR,
  OPTIONAL_PICK_COLOR
} from '../../fields.constants';
import {
  formatCoverage,
  formatCount,
  formatLongDay,
  surveyLabel,
  surveyDaysOpen,
  exportRateLabel,
  effectiveMustPickCm,
  exportedStoneCounts,
  frozenStoneCounts
} from '../../fields.utils';
import {
  Splash,
  Box,
  Big,
  Caption,
  Rows,
  Item,
  Section,
  SectionTitle,
  Split,
  SplitItem,
  AgainButton,
  Note,
  ErrorNote
} from './FrozenPanel.styles';

const NO_VALUE = '—';

export const FrozenPanel = ({ survey, surveyDetail, precision, today, timezone }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);
  const { data: stones } = useGetSurveyStonesQuery(survey.id);
  const acres =
    surveyDetail && Math.round(surveyDetail.survey.area_acres).toLocaleString();

  const { export: file } = survey;
  const mustPickCm = effectiveMustPickCm({ survey, isRecord: true });
  const counts = exportedStoneCounts(stones, mustPickCm);
  const record = frozenStoneCounts(stones, mustPickCm);
  const areaAcres = surveyDetail?.survey.area_acres;
  const density = areaAcres ? (survey.stone_count / areaAcres).toFixed(2) : '0';

  const downloadAgain = async () => {
    setIsDownloading(true);
    setError(null);
    try {
      await downloadExport({ surveyId: survey.id, format: file.format });
    } catch (downloadError) {
      setError('Could not download the file. Try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <aside data-testid='frozen-panel'>
      <Section data-testid='frozen-export'>
        <SectionTitle>What went to the picker</SectionTitle>
        {file ? (
          <>
            <Box>
              <Big>{formatCount(file.stones_selected)}</Big>
              <Caption>
                stones in the file, downloaded{' '}
                {formatLongDay(getAccountToday(timezone, new Date(file.created_at)))}
              </Caption>
              <Split>
                <SplitItem $color={MUST_PICK_COLOR}>
                  <i />
                  {formatCount(counts.red)} red
                </SplitItem>
                <SplitItem $color={OPTIONAL_PICK_COLOR}>
                  <i />
                  {formatCount(counts.yellow)} yellow
                </SplitItem>
              </Split>
            </Box>
            <Rows $compact>
              <Item>
                <span>File</span>
                <b>{file.filename}</b>
              </Item>
              <Item>
                <span>
                  <HighRiskTerm mustPickCm={mustPickCm} /> from
                </span>
                <b>{mustPickCm} cm</b>
              </Item>
              <Item>
                <span>Removal rate</span>
                <b>{exportRateLabel(file.cap_per_acre)}</b>
              </Item>
              <Item>
                <span>Left on the field</span>
                <b>{formatCount(file.held_back)}</b>
              </Item>
            </Rows>
            <AgainButton type='button' disabled={isDownloading} onClick={downloadAgain}>
              {isDownloading ? 'Preparing…' : 'Download this file again'}
            </AgainButton>
            {error && <ErrorNote role='alert'>{error}</ErrorNote>}
            <Note>
              Rebuilt from the same high-risk size and rate, against the stones this
              survey held. Same points, same order.
            </Note>
          </>
        ) : (
          <Note $first>
            No file was generated from this survey before it was finalised. The stones on
            the map are the record of what was found.
          </Note>
        )}
      </Section>
      <Splash>
        <b>{surveyLabel(survey)}</b>
        Finalised on {formatLongDay(survey.closed_on)}. Kept as it stood that day so you
        can see what you were working from
        {file ? ', and download the same file again' : ''}.
      </Splash>
      <Box>
        <Big>{formatCount(survey.stone_count)}</Big>
        <Caption>
          stones on record{acres ? ` over ${acres} acres` : ''}, {density} per acre
        </Caption>
      </Box>
      <Rows>
        {STONE_CLASSES.map(({ key, label }) => (
          <Item key={key}>
            <span>{label}</span>
            <b>{stones ? formatCount(record.byClass[key] ?? 0) : NO_VALUE}</b>
          </Item>
        ))}
        <Item>
          <span>Red ({mustPickCm} cm+)</span>
          <b>{stones ? formatCount(record.red) : NO_VALUE}</b>
        </Item>
        <Item>
          <span>Ground imaged</span>
          <b>
            {survey.coverage_ratio == null
              ? NO_VALUE
              : formatCoverage(survey.coverage_ratio, precision)}
          </b>
        </Item>
        <Item>
          <span>Passes</span>
          <b>{survey.pass_count}</b>
        </Item>
        <Item>
          <span>Open for</span>
          <b>{surveyDaysOpen(survey, today)} days</b>
        </Item>
      </Rows>
    </aside>
  );
};
